import { useTranslation } from "react-i18next";
import { MessageOutlined } from "@ant-design/icons";
import { CommentsHook } from "@hooks";
import { Drawer, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Styles from "./CommentsDrawer.styles";
import { SharedSpinner } from "./SharedSpinner";

dayjs.extend(relativeTime);

const { Text } = Typography;

interface Props {
  open: boolean;
  commentsHook: CommentsHook;
  averageRate: number;
  rateCount: number;
  onClose: () => void;
}

const CommentsDrawer: React.FC<Props> = ({
  open,
  commentsHook,
  averageRate,
  rateCount,
  onClose,
}) => {
  const { t } = useTranslation();
  const { comments, loading, hasMore, loadMore, loadingMore } = commentsHook;

  return (
    <Drawer
      title={t("CustomerReviews")}
      placement="right"
      onClose={onClose}
      open={open}
      size={840}
      styles={{
        body: { padding: "16px 24px" },
      }}
    >
      <SharedSpinner loading={loading}>
        <Styles.Header>
          <Styles.AverageRate level={2}>
            {averageRate.toFixed(1)}
          </Styles.AverageRate>
          <Styles.AverageStars disabled allowHalf value={averageRate} />
          <br />
          <Styles.ReviewCount>
            {t("BasedOnReviews").replace("{0}", String(rateCount))}
          </Styles.ReviewCount>
        </Styles.Header>

        {comments.length === 0 ? (
          <Styles.EmptyWrapper>
            <Styles.EmptyIcon>
              <MessageOutlined />
            </Styles.EmptyIcon>
            <Text type="secondary">{t("NoReviews")}</Text>
          </Styles.EmptyWrapper>
        ) : (
          <>
            <Styles.ReviewList>
              {comments.map((comment, index) => (
                <Styles.ReviewCard key={index} $index={index}>
                  <Styles.ReviewStars disabled allowHalf value={comment.rate} />
                  <Styles.ReviewDate>
                    {dayjs(comment.creationTime).fromNow()}
                  </Styles.ReviewDate>
                  <Styles.ReviewComment>{comment.comment}</Styles.ReviewComment>
                </Styles.ReviewCard>
              ))}
            </Styles.ReviewList>

            {hasMore && (
              <Styles.LoadMoreWrapper>
                <Styles.LoadMoreButton
                  type="primary"
                  block
                  loading={loadingMore}
                  onClick={loadMore}
                >
                  {t("LoadMore")}
                </Styles.LoadMoreButton>
              </Styles.LoadMoreWrapper>
            )}
          </>
        )}
      </SharedSpinner>
    </Drawer>
  );
};

export default CommentsDrawer;
